import { Checkbox, NumberInput, Stack, Text } from '@mantine/core'
import { formatGap, formatLapTime, lapColumns } from '@racedash/shared'
import type { LapColumn, LapDoc } from '@racedash/shared'
import { Tile } from '../Tile'
import type { WidgetEditorProps, WidgetProps } from './props'

const headings: Record<LapColumn, string> = {
  lap: 'Lap',
  time: 'Time',
  s1: 'S1',
  s2: 'S2',
  s3: 'S3',
  position: 'Pos',
  gap: 'Gap',
}

/** Relative widths, so a lap number does not get the same room as a gap. */
const weights: Record<LapColumn, number> = {
  lap: 0.6,
  time: 1.2,
  s1: 1,
  s2: 1,
  s3: 1,
  position: 0.6,
  gap: 1.1,
}

function cell(lap: LapDoc, column: LapColumn): string {
  switch (column) {
    case 'lap':
      return String(lap.lap)
    case 'time':
      return formatLapTime(lap.lapTimeMs)
    case 's1':
      return formatLapTime(lap.s1Ms)
    case 's2':
      return formatLapTime(lap.s2Ms)
    case 's3':
      return formatLapTime(lap.s3Ms)
    case 'position':
      return lap.position && lap.position > 0 ? String(lap.position) : '—'
    case 'gap':
      return formatGap(lap.gapToLeaderMs, lap.gapToLeaderLaps ?? 0)
  }
}

/**
 * The last few laps, most recent first.
 *
 * The lap in progress is included and marked rather than hidden. It is the lap
 * anyone reading this actually cares about, and the sector times are already
 * there — dropping it until the car crosses the line would blank the top row
 * for a couple of minutes every lap.
 */
export default function LapList({ config, data }: WidgetProps<'lapList'>) {
  const laps = data.laps.slice(0, config.rows)

  if (laps.length === 0) {
    return (
      <Tile label="Recent laps">
        <span className="dash-dead-text">No laps yet</span>
      </Tile>
    )
  }

  return (
    <Tile label="Recent laps">
      <table className="dash-table">
        <colgroup>
          {config.columns.map((c) => (
            <col key={c} style={{ width: `${(weights[c] / total(config.columns)) * 100}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {config.columns.map((c) => (
              <th key={c}>{headings[c]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {laps.map((lap) => (
            <tr
              key={lap.lap}
              data-partial={lap.lapTimeMs == null || lap.lapTimeMs <= 0 ? 'true' : undefined}
            >
              {config.columns.map((c) => (
                <td key={c}>{cell(lap, c)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Tile>
  )
}

function total(columns: readonly LapColumn[]): number {
  return columns.reduce((sum, c) => sum + weights[c], 0) || 1
}

/**
 * Row count and columns.
 *
 * The column order is the author's and is preserved, so unticking a column and
 * ticking it again puts it back on the end rather than back where it was. That
 * is the honest behaviour for a list of checkboxes, and reordering wants
 * dragging, which this is not the place for.
 */
export function Editor({ config, onChange }: WidgetEditorProps<'lapList'>) {
  const toggle = (column: LapColumn, on: boolean) => {
    const next = on
      ? [...config.columns, column]
      : config.columns.filter((c) => c !== column)
    // Never all the way to nothing: a lap list with no columns is a blank tile
    // with a heading, and there is no way back from it inside this form.
    if (next.length === 0) return
    onChange({ ...config, columns: next })
  }

  return (
    <Stack gap="sm">
      <NumberInput
        label="Laps to show"
        min={1}
        max={60}
        value={config.rows}
        onChange={(v) => onChange({ ...config, rows: typeof v === 'number' ? v : config.rows })}
      />
      <div>
        <Text size="sm" fw={500} mb={4}>
          Columns
        </Text>
        <Stack gap={4}>
          {lapColumns.map((c) => (
            <Checkbox
              key={c}
              label={headings[c]}
              checked={config.columns.includes(c)}
              onChange={(e) => toggle(c, e.currentTarget.checked)}
            />
          ))}
        </Stack>
      </div>
    </Stack>
  )
}
